import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Divider,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  ArrowBack,
} from '@mui/icons-material';
import { authService } from '@services/authService';

const RegisterClient: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    setFieldErrors({});

    // Validare confirmare parola
    if (formData.password !== formData.confirmPassword) {
      setFieldErrors({ password: 'Parolele nu se potrivesc', confirmPassword: 'Parolele nu se potrivesc' });
      setLoading(false);
      return;
    }

    try {
      const response = await authService.register({
        ...formData,
        role: 'client'
      });

      if (response.success) {
        setSuccess(response.message || 'Cont creat cu succes! Verifica emailul pentru a-ti activa contul.');
        // Nu mai redirectionam automat - utilizatorul trebuie sa activeze contul
      } else {
        if (response.field) {
          setFieldErrors({ [response.field]: response.error || 'Eroare necunoscuta' });
        } else {
          setError(response.error || 'Eroare la crearea contului');
        }
      }
    } catch (err) {
      setError('Eroare la crearea contului. Incearca din nou.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {/* Header */}
        <Box display="flex" alignItems="center" mb={3}>
          <IconButton onClick={() => navigate('/account-type')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" component="h1" color="primary">
            Creeaza cont Client
          </Typography>
        </Box>

        <Typography variant="body1" color="text.secondary" mb={4}>
          Completeaza formularul de mai jos pentru a crea un cont
        </Typography>

        {/* Manual Registration Form */}
        <Box component="form" onSubmit={handleSubmit}>
          <Typography variant="h6" gutterBottom>
            Inregistrare manuala
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box display="flex" gap={2} mb={2}>
            <TextField
              name="firstName"
              label="Prenume"
              value={formData.firstName}
              onChange={handleInputChange}
              required
              fullWidth
              disabled={loading}
              error={!!fieldErrors.firstName}
              helperText={fieldErrors.firstName}
            />
            <TextField
              name="lastName"
              label="Nume"
              value={formData.lastName}
              onChange={handleInputChange}
              required
              fullWidth
              disabled={loading}
              error={!!fieldErrors.lastName}
              helperText={fieldErrors.lastName}
            />
          </Box>

          <TextField
            name="email"
            label="Email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            fullWidth
            disabled={loading}
            sx={{ mb: 2 }}
            error={!!fieldErrors.email}
            helperText={fieldErrors.email}
          />

          <TextField
            name="phone"
            label="Telefon"
            value={formData.phone}
            onChange={handleInputChange}
            fullWidth
            disabled={loading}
            sx={{ mb: 2 }}
            error={!!fieldErrors.phone}
            helperText={fieldErrors.phone}
          />

          <TextField
            name="password"
            label="Parola"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleInputChange}
            required
            fullWidth
            disabled={loading}
            sx={{ mb: 2 }}
            error={!!fieldErrors.password}
            helperText={fieldErrors.password}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            name="confirmPassword"
            label="Confirma parola"
            type={showConfirmPassword ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={handleInputChange}
            required
            fullWidth
            disabled={loading}
            sx={{ mb: 3 }}
            error={!!fieldErrors.confirmPassword}
            helperText={fieldErrors.confirmPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={loading}
            sx={{ mb: 2 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Creeaza cont'}
          </Button>
        </Box>

        <Box textAlign="center" mt={3}>
          <Typography variant="body2" color="text.secondary">
            Ai deja un cont?{' '}
            <Button
              variant="text"
              color="primary"
              onClick={() => navigate('/login')}
              sx={{ textTransform: 'none' }}
            >
              Conecteaza-te aici
            </Button>
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default RegisterClient; 
